import { useEffect, useMemo, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";
import EmailStatusBanner from "@/components/email/EmailStatusBanner";
import { useToast } from "@/components/ToastProvider";
import { useEmailStatus } from "@/hooks/useEmailStatus";
import { linkImapEmail } from "@/api/onboarding";
import { disconnectImap, testImap } from "@/api/emailSettings";

const initialForm = {
  email_address: "",
  imap_host: "",
  imap_port: 993,
  use_ssl: true,
  username: "",
  password: "",
};

type EmailFormState = typeof initialForm;

export default function EmailSettingsPage() {
  const { status, loading: statusLoading, refresh: refreshStatus } = useEmailStatus();
  const toast = useToast();
  const [form, setForm] = useState<EmailFormState>({ ...initialForm });
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing) return;
    setForm((prev) => ({
      ...prev,
      email_address: status?.email_address ?? "",
      imap_host: status?.imap_host ?? "",
      imap_port: status?.imap_port ?? 993,
      use_ssl: status?.use_ssl ?? true,
      username: status?.username ?? status?.email_address ?? "",
      password: "",
    }));
  }, [isEditing, status]);

  const configured = useMemo(() => status?.status !== "not_configured", [status]);

  const handleFormChange = (field: keyof EmailFormState, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleConnect = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    setStatusMessage(null);
    try {
      const response = await linkImapEmail({
        email_address: form.email_address.trim(),
        imap_host: form.imap_host.trim(),
        imap_port: form.imap_port,
        use_ssl: form.use_ssl,
        username: form.username.trim() || form.email_address.trim(),
        password: form.password,
      });
      const message = response.message || "Connection attempt complete.";
      toast.push({ title: "Email", message });
      setStatusMessage(message);
      setIsEditing(false);
      setForm((prev) => ({ ...prev, password: "" }));
      await refreshStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to connect to IMAP.";
      setFormError(message);
      toast.push({ title: "Email", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const result = await testImap();
      const title = result.status === "connected" ? "IMAP connection verified" : "IMAP test failed";
      const message = result.message || title;
      toast.push({ title: "Email", message });
      setStatusMessage(message);
      await refreshStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to test IMAP connection.";
      toast.push({ title: "Email", message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectImap();
      const message = "Email integration disconnected.";
      toast.push({ title: "Email", message });
      setStatusMessage(message);
      setIsEditing(true);
      setForm((prev) => ({ ...prev, password: "" }));
      await refreshStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to disconnect email.";
      toast.push({ title: "Email", message });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const renderSummary = () => (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="aa-glass-card p-4 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
          <p className="text-lg font-semibold text-foreground">{status?.status}</p>
          {status?.message && <p className="text-xs text-muted-foreground">{status.message}</p>}
        </div>
        <div className="aa-glass-card p-4 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Email address</p>
          <p className="text-lg font-semibold text-foreground">{status?.email_address ?? "—"}</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">IMAP host</p>
          <p className="text-sm text-foreground">{status?.imap_host ?? "—"}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Port</p>
          <p className="text-sm text-foreground">{status?.imap_port ?? "—"}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Username</p>
          <p className="text-sm text-foreground">{status?.username ?? "—"}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">SSL/TLS</p>
          <p className="text-sm text-foreground">{status?.use_ssl ? "Enabled" : "Disabled"}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setIsEditing((prev) => !prev)}>
          {isEditing ? "Hide credentials" : "Update credentials"}
        </Button>
        <Button variant="secondary" onClick={handleTestConnection} disabled={isTesting}>
          {isTesting ? "Testing…" : "Test connection"}
        </Button>
        <Button variant="ghost" onClick={handleDisconnect} disabled={isDisconnecting}>
          Disconnect
        </Button>
      </div>
      {statusMessage && <p className="text-xs text-emerald-300">{statusMessage}</p>}
    </div>
  );

  const renderForm = () => (
    <form className="space-y-5" onSubmit={handleConnect}>
      <div>
        <Label>Email address</Label>
        <Input
          required
          type="email"
          value={form.email_address}
          onChange={(event) => handleFormChange("email_address", event.target.value)}
        />
      </div>
      <div>
        <Label>IMAP host</Label>
        <Input
          required
          value={form.imap_host}
          onChange={(event) => handleFormChange("imap_host", event.target.value)}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Port</Label>
          <Input
            required
            type="number"
            min={1}
            value={form.imap_port}
            onChange={(event) => handleFormChange("imap_port", Number(event.target.value) || 993)}
          />
        </div>
        <div>
          <Label>Use SSL/TLS</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.use_ssl}
              onCheckedChange={(checked) => handleFormChange("use_ssl", Boolean(checked))}
            />
            <span className="text-sm text-foreground/70">Use a secure connection</span>
          </div>
        </div>
      </div>
      <div>
        <Label>Username</Label>
        <Input
          required
          value={form.username}
          onChange={(event) => handleFormChange("username", event.target.value)}
        />
      </div>
      <div>
        <Label>Password</Label>
        <Input
          required
          type="password"
          value={form.password}
          onChange={(event) => handleFormChange("password", event.target.value)}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Use an app password if your provider requires it—Americana Agents currently connects via IMAP.
      </p>
      {formError && <p className="text-xs text-rose-400">{formError}</p>}
      {statusMessage && <p className="text-xs text-emerald-300">{statusMessage}</p>}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save & test"}
        </Button>
        {configured && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
            Done
          </Button>
        )}
      </div>
    </form>
  );

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader title="Sync Email with IMAP" description="Sync your inbox via IMAP to enable replies and forwarding." />
      <EmailStatusBanner status={status} />
      <section className="aa-glass-card p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Email integration</h2>
          <p className="text-xs text-muted-foreground">Keep your inbox synced for sends, replies, and forwarding.</p>
        </div>
        {statusLoading ? (
          <p className="text-sm text-muted-foreground">Checking integration status…</p>
        ) : configured ? (
          <>{renderSummary()}{isEditing && renderForm()}</>
        ) : (
          renderForm()
        )}
      </section>
    </div>
  );
}
